import React, { useState } from 'react';
import { Box, Button, Divider, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import {
  ANNOTATION_EXPORT_FORMATS,
  AnnotationExportFormat,
  ExportableAnnotation,
  buildAnnotationExport,
} from '../../utils/annotationExport';
import { downloadTextFile } from '../../utils/download';
import { formatFrameRate } from '../../utils/timecode';

interface MarkerExportMenuProps {
  annotations: ExportableAnnotation[];
  fps: number;
  /** Estimated (not measured) frame rate: say so in the menu. */
  fpsAssumed?: boolean;
  title: string;
  videoId?: string;
  sourceName?: string;
  label?: string;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium';
  formats?: AnnotationExportFormat[];
  onExported?: (filename: string) => void;
}

/** "Export" menu: NLE markers (Premiere CSV, EDL), spreadsheet CSV and JSON. */
const MarkerExportMenu: React.FC<MarkerExportMenuProps> = ({
  annotations,
  fps,
  fpsAssumed = false,
  title,
  videoId,
  sourceName,
  label = 'Export',
  variant = 'outlined',
  size = 'small',
  formats,
  onExported,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const options = ANNOTATION_EXPORT_FORMATS.filter((option) => !formats || formats.includes(option.format));

  const run = (format: AnnotationExportFormat) => {
    setAnchor(null);
    const file = buildAnnotationExport(format, annotations, { fps, title, videoId, sourceName });
    downloadTextFile(file.filename, file.content, file.mimeType);
    onExported?.(file.filename);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<FileDownloadOutlinedIcon />}
        endIcon={<ExpandMoreIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={annotations.length === 0}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 300 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Timecodes at {formatFrameRate(fps)} fps{fpsAssumed ? ' (estimated — set the frame rate if your footage differs)' : ''}.
          </Typography>
        </Box>
        <Divider />
        {options.map((option) => (
          <MenuItem key={option.format} onClick={() => run(option.format)}>
            <ListItemText
              primary={option.label}
              secondary={option.description}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MarkerExportMenu;
